import React, { useState, useEffect, useContext, useCallback } from "react";
import { Row, Col, Form, Input, Spin, Select } from "antd";
import AddPhysicianModalWrapper from "../../../modal/AddPhysicianModalWrapper";
import {
  CustomButton,
  CustomSecondaryButton,
} from "../../physician_page/styled";
import { LoadingOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { SpecilityContext } from "../../../../context/SpecilityContext";
import { limitedcountry, validateZipCode } from "../../../../utils/utils";
import { debounce, notify } from "../../../../utils/helper";
import { ccmManagerService } from "../../../../services/user";
import { extractToken } from "../../../../utils/localStorage";
import { globalRoles } from "../../../../utils/roles";

const { Option } = Select;

const FormContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;
  width: 100%;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  /* &::-webkit-scrollbar-track {
    background:rgb(138, 136, 136);
    border-radius: 10px;
  } */

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  // .ant-select-item-option-content {
  //   display: flex;
  //   justify-content: start;
  //   gap: 5px;
  // }

  .customZipSpin {
    position: absolute;
    z-index: 1;
    top: 10px;
    right: 10px;
  }
`;

const AddManagerModal = ({
  isOpen,
  setIsOpen,
  isEdit,
  setIsEdit,
  selectedManagerId,
  setSelectedManagerId,
  fetchManagers,
  loading,
  setLoading,
  coordinatorList,
}) => {
  const [form] = Form.useForm();
  const user = extractToken();
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;

  const { specialtyOptions } = useContext(SpecilityContext);
  const [countryName, setCountryName] = useState("");
  const [zip, setZip] = useState("");
  const [message, setMessage] = useState("");
  const [zipData, setZipData] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [managerDataById, setManagerDataById] = useState([]);

  const createCCMManager = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        const payload = {
          ...values,
          zip: zip,
          timeZone: zipData || "",
        };
        const res = await ccmManagerService.update(selectedManagerId, payload);
        notify("success", "CCM Manager updated successfully");
      } else {
        const payload = {
          ...values,
          zip: zip,
          timeZone: zipData || "",
        };
        const res = await ccmManagerService.create(payload);
        notify("success", "CCM Manager created successfully");
      }
      setIsOpen(false);
      setIsEdit(false);
      setSelectedManagerId("");
      fetchManagers();
      setLoading(false);
    } catch (err) {
      notify("error", err?.data);
      setLoading(false);
    }
  };

  const getManagerById = async () => {
    try {
      const res = await ccmManagerService.getById(selectedManagerId);
      setManagerDataById(res?.data);
    } catch (err) {
      notify("error", err?.data);
    }
  };

  useEffect(() => {
    if (selectedManagerId && isEdit) {
      getManagerById();
    }
  }, [selectedManagerId]);

  const validateDebounced = useCallback(
    debounce(async (zipVal, countryCodeVal) => {
      setZipLoading(true);
      try {
        const msg = await validateZipCode(zipVal, countryCodeVal);
        setMessage(msg.message);
        setZipData(msg.data);
      } catch (err) {
        setMessage("Error validating ZIP");
        setZipLoading(false);
      } finally {
        setZipLoading(false);
      }
    }, 600),
    []
  );

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        name: managerDataById?.name || "",
        specialty: managerDataById?.specialty || "",
        email: managerDataById?.email || "",
        zip: managerDataById?.zip || "",
        country: managerDataById?.country || "",
        ccm: managerDataById?.parent?.id || undefined,
      });
    } else {
      form.setFieldsValue({
        name: "",
        specialty: undefined,
        email: "",
        zip: "",
        country: undefined,
        ccm: undefined,
      });
    }
  }, [managerDataById, form, isEdit]);

  useEffect(() => {
    if (managerDataById) {
      setCountryName(managerDataById?.country);
      setZip(managerDataById?.zip);
      setZipData(managerDataById?.timeZone);
    }
  }, [managerDataById]);

  const handleZipChange = (e) => {
    const value = e.target.value;
    setZip(value);
    if (value.length >= 3) {
      validateDebounced(value, countryName);
    } else {
      setMessage("");
      setZipData(null);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setIsEdit(false);
    setSelectedManagerId("");
    // setPreviewData([]);
    // setIsPreviewMode(false);
    form.resetFields();
  };

  return (
    <AddPhysicianModalWrapper
      show={isOpen}
      setShow={onClose}
      title={
        isEdit ? (
          <div className="row-flex flex-column">
            <h3 className="font-18 font-semibold text-primary mb-5">
              Update CCM Manager
            </h3>
            <span className="font-15 text-secondry">
              Edit the CCM manager's information below.
            </span>
          </div>
        ) : (
          <div className="row-flex flex-column">
            <h3 className="font-18 font-semibold text-primary mb-5">
              Add New CCM Manager
            </h3>
            <span className="font-15 text-secondry">
              Enter the details to add a new CCM manager to the platform.
            </span>
          </div>
        )
      }
      footer={false}
      width={700}
      keyClose={false}
      className="custom-detail-modal"
    >
      <Form form={form} layout="vertical" onFinish={createCCMManager}>
        <FormContainer>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  { required: true, message: "Please input the full name!" },
                ]}
              >
                <Input placeholder="Enter Full Name" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please input your email!" },
                ]}
              >
                <Input placeholder="Enter Email Address" size="large" />
              </Form.Item>
            </Col>

            {isSuperAdmin && (
              <Col span={24}>
                <Form.Item
                  name="ccm"
                  label="Coordinator"
                  rules={[{ required: true, message: "Please select a coordinator!" }]}
                >
                  <Select
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    placeholder="Select Coordinator"
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {coordinatorList?.map((item) => (
                      <Option
                        key={item.id}
                        value={item.id}
                        label={`${item.name} - ${item.email}`}
                      >
                        {item.name} - {item.email}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {/* <Col span={9}>
              <Form.Item
                name="specialty"
                label="Medical Specialty"
                rules={[
                  { required: true, message: "Please select a specialty!" },
                ]}
              >
                <Select
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  placeholder="Select Medical Specialty"
                  showSearch
                  style={{ width: "100%" }}
                  dropdownStyle={{
                    maxHeight: 200,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  listHeight={160}
                  options={specialtyOptions}
                  size="large"
                  loading={loading}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col> */}

            <Col span={12}>
              <Form.Item
                name="country"
                label="Country"
                rules={[
                  { required: true, message: "Please select a country!" },
                ]}
              >
                <Select
                  style={{ width: "100%" }}
                  showSearch
                  placeholder="Select Country"
                  dropdownStyle={{
                    maxHeight: 200,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  listHeight={160}
                  value={countryName}
                  onChange={(value) => setCountryName(value)}
                  optionLabelProp="label"
                  filterOption={(input, option) =>
                    option?.label.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {limitedcountry.map((country) => (
                    <Option value={country.code} label={country.name}>
                      <span>{country.name}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="zip"
                label="Zip Code"
                rules={[
                  () => ({
                    validator(_, value) {
                      if (!zip) {
                        return Promise.reject(
                          new Error("Please input the zip code!")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  type="text"
                  placeholder="e.g., 560034"
                  value={zip}
                  onChange={handleZipChange}
                  loading={zipLoading}
                />
                {zipLoading ? (
                  <Spin
                    indicator={<LoadingOutlined spin />}
                    className="customZipSpin"
                  />
                ) : (
                  <span className="customZipSpin font-12 text-primary">
                    {zipData && zipData}
                  </span>
                )}
                {message && (
                  <p
                    className="m-0"
                    style={{
                      color: zipData ? "green" : "red",
                      fontSize: "13px",
                    }}
                  >
                    {message}
                  </p>
                )}
              </Form.Item>
            </Col>
          </Row>
        </FormContainer>

        <Row>
          <Col span={24} className="d-flex justify-end gap-12">
            <CustomSecondaryButton onClick={onClose}>
              Cancel
            </CustomSecondaryButton>
            <CustomButton
              htmlType="submit"
              loading={loading}
              disabled={zipData === null}
            >
              {loading ? "Processing..." : isEdit ? "Update" : "Add"}
            </CustomButton>
          </Col>
        </Row>
      </Form>
    </AddPhysicianModalWrapper>
  );
};

export default AddManagerModal;
